// Settings > Account: two-step sign-in with emailed codes, and remembered devices.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Monitor, ShieldCheck, Smartphone } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../api/client';
import type { TwoFactorStatus } from '../types';

function detail(err: unknown, fallback: string) {
  return (axios.isAxiosError(err) && (err.response?.data as { detail?: string } | undefined)?.detail) || fallback;
}

function when(value: string) {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TwoFactorSettings() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [step, setStep] = useState<'idle' | 'code' | 'disable'>('idle');
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authAPI.twoFactorStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(detail(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = () => {
    if (busy || code.length !== 6) return;
    void run(async () => {
      setStatus(await authAPI.twoFactorAction('confirm', { challenge, code }));
      setStep('idle');
      toast.success('Two-step sign-in is on.');
    }, "That code didn't work.");
  };

  const disable = () => {
    if (busy || !password) return;
    void run(async () => {
      setStatus(await authAPI.twoFactorAction('disable', { password }));
      setPassword('');
      setStep('idle');
      toast.success('Two-step sign-in is off.');
    }, "Couldn't turn it off.");
  };

  if (!status) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-2 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" />
        Loading security settings…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
          <ShieldCheck size={20} />
          Two-step sign-in
        </h2>
        <p className="text-sm text-gray-500">
          After your password, we email a 6-digit code to {status.email_hint} so no one can sign in with
          your password alone.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-navy">{status.enabled ? 'On' : 'Off'}</p>
          {status.required_by_role && (
            <p className="text-xs text-gray-500">Required for super admins, so it stays on.</p>
          )}
        </div>
        {!status.required_by_role && step === 'idle' && (
          status.enabled ? (
            <button
              type="button"
              onClick={() => setStep('disable')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
            >
              Turn off
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(async () => {
                const started = await authAPI.twoFactorAction('start');
                setChallenge(started.challenge || '');
                setCode('');
                setStep('code');
              }, "Couldn't send a code.")}
              className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              Turn on
            </button>
          )
        )}
      </div>

      {step === 'code' && (
        <div className="space-y-2">
          <p className="text-sm text-gray-700">Enter the code we just emailed you.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                confirmCode();
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="6-digit code"
              placeholder="000000"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg tracking-[0.4em] font-semibold focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            <button
              type="button"
              disabled={busy || code.length !== 6}
              onClick={confirmCode}
              className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              Confirm
            </button>
            <button type="button" onClick={() => setStep('idle')} className="px-4 py-2 text-sm text-gray-500">
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'disable' && (
        <div className="space-y-2">
          <p className="text-sm text-gray-700">Enter your password to turn off two-step sign-in.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                disable();
              }}
              autoComplete="current-password"
              aria-label="Password"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
            />
            <button
              type="button"
              disabled={busy || !password}
              onClick={disable}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              Turn off
            </button>
            <button type="button" onClick={() => setStep('idle')} className="px-4 py-2 text-sm text-gray-500">
              Cancel
            </button>
          </div>
        </div>
      )}

      {status.enabled && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-navy">Remembered devices</p>
            {status.devices.length > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(async () => {
                  setStatus(await authAPI.twoFactorAction('forget_all'));
                  toast.success('Every device will need a code next time.');
                }, "Couldn't forget devices.")}
                className="text-xs font-medium text-red-600 hover:underline"
              >
                Forget all
              </button>
            )}
          </div>
          {status.devices.length === 0 ? (
            <p className="text-sm text-gray-400">
              None. Check "Remember this device" when you sign in to skip the code for 30 days.
            </p>
          ) : (
            <ul className="space-y-2">
              {status.devices.map((device) => {
                const Icon = /iphone|android|app/i.test(device.label) ? Smartphone : Monitor;
                return (
                  <li key={device.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon size={18} className="text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800 truncate">{device.label}</p>
                        <p className="text-xs text-gray-400">Last used {when(device.last_used_at || device.created_at)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void run(async () => {
                        setStatus(await authAPI.twoFactorAction('forget_device', { device_id: device.id }));
                      }, "Couldn't forget that device.")}
                      className="text-xs font-medium text-gray-500 hover:text-red-600 shrink-0"
                    >
                      Forget
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
